import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';

interface FeatureFlag {
  key: string;
  value: boolean | number;
  label: string;
  description: string;
  type: 'boolean' | 'number';
}

const FLAG_META: Record<string, { label: string; description: string; type: 'boolean' | 'number' }> = {
  signup_enabled: { label: 'Signup', description: 'Allow new user registrations', type: 'boolean' },
  maintenance_mode: { label: 'Maintenance Mode', description: 'Show maintenance screen to all users', type: 'boolean' },
  translation_enabled: { label: 'Live Translation', description: 'Enable chat message translation and language preferences', type: 'boolean' },
  video_upload_enabled: { label: 'Video Upload', description: 'Allow users to upload videos', type: 'boolean' },
  allow_promo_codes: { label: 'Promo Codes', description: 'Allow promotional code redemption', type: 'boolean' },
  allow_guest_access: { label: 'Guest Access', description: 'Allow guest users without accounts', type: 'boolean' },
  max_upload_size_mb: { label: 'Max Upload Size (MB)', description: 'Maximum file upload size in megabytes', type: 'number' },
  new_user_trial_days: { label: 'Trial Days', description: 'Number of free trial days for new users', type: 'number' },
};

export const FeatureFlagsManager: React.FC = () => {
  const [flags, setFlags] = useState<Record<string, boolean | number>>({});
  const [originalFlags, setOriginalFlags] = useState<Record<string, boolean | number>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.id) {
      loadFlags();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadFlags = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await adminApi.getFeatureFlags();

      if (!result.success) {
        if (result.isAuthError) {
          await signOut();
          navigate('/login');
          return;
        }
        throw new Error(adminApi.handleApiError(result));
      }

      const data = result.data?.features || {};
      setFlags(data);
      setOriginalFlags(data);
    } catch (err) {
      console.error('Error loading feature flags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: string) => {
    setFlags(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleNumberChange = (key: string, value: string) => {
    setFlags(prev => ({ ...prev, [key]: parseInt(value) || 0 }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const result = await adminApi.setFeatureFlags(flags);

      if (!result.success) {
        throw new Error(adminApi.handleApiError(result));
      }

      setOriginalFlags(flags);
      setSuccess('Feature flags saved successfully');
    } catch (err) {
      console.error('Error saving feature flags:', err);
      setError(err instanceof Error ? err.message : 'Failed to save feature flags');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(flags) !== JSON.stringify(originalFlags);

  const flagList: FeatureFlag[] = Object.entries(flags).map(([key, value]) => ({
    key,
    value,
    label: FLAG_META[key]?.label || key,
    description: FLAG_META[key]?.description || '',
    type: FLAG_META[key]?.type || (typeof value === 'boolean' ? 'boolean' : 'number'),
  }));

  const booleanFlags = flagList.filter(f => f.type === 'boolean');
  const numberFlags = flagList.filter(f => f.type === 'number');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-theme-primary">Feature Flags</h2>
          <p className="text-theme-secondary mt-1">Toggle features on or off for all users</p>
        </div>
        {hasChanges && (
          <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">Unsaved changes</span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-md text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 hover:underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-md text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2 hover:underline">Dismiss</button>
        </div>
      )}

      {/* Boolean Toggles */}
      <div className="card-themed rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-theme-primary">Toggles</h3>
        <div className="space-y-1">
          {booleanFlags.map(flag => (
            <div
              key={flag.key}
              className="flex items-center justify-between p-4 rounded-lg hover:bg-theme-secondary/5 transition-colors"
            >
              <div>
                <div className="font-medium text-theme-primary">{flag.label}</div>
                {flag.description && (
                  <div className="text-sm text-theme-secondary">{flag.description}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleToggle(flag.key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  flag.value ? 'bg-blue-600' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    flag.value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Number Settings */}
      {numberFlags.length > 0 && (
        <div className="card-themed rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-theme-primary">Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {numberFlags.map(flag => (
              <div key={flag.key} className="p-4 rounded-lg bg-theme-secondary/5">
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {flag.label}
                </label>
                {flag.description && (
                  <p className="text-xs text-theme-secondary mb-2">{flag.description}</p>
                )}
                <input
                  type="number"
                  value={flag.value as number}
                  onChange={(e) => handleNumberChange(flag.key, e.target.value)}
                  min="0"
                  className="w-full input"
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex gap-4">
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {saving && (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          )}
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={() => { setFlags(originalFlags); }}
          disabled={!hasChanges}
          className="px-6 py-2 btn-secondary disabled:opacity-50"
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default FeatureFlagsManager;
